import { useEffect, useRef } from 'react';
import { useReducedMotion } from 'framer-motion';
import { cn } from 'root/src/client/v2/common/helpers/cn';
// Tunables - tweak freely while testing the look. Sizes are in CSS pixels. Kept intentionally faint:
// this is ambient texture, not a feature, so it must stay low-contrast against the dark background.
const CELL = 24; // square edge length
const GAP = 12; // gap between squares
const BASE_ALPHA = 0.005; // resting brightness of every square
const WAVE_ALPHA = 0.01; // extra brightness added by the travelling diagonal wave
const WAVE_SPEED = 0.0002; // wave phase speed (radians per ms) - very slow, barely-there drift
const WAVE_FREQUENCY = 0.05; // spatial tightness of the wave bands (higher = narrower bands)
const SPARKLE_ALPHA = 0.014; // peak brightness of a randomly igniting cell (barely-there shimmer)
const SPARKLE_SHARPNESS = 14; // higher = rarer, sharper flashes
const BLOOM_THRESHOLD = 0.6; // cells brighter than this get a soft glow halo (effectively off at these alphas)
// Muted, desaturated purple-grey close to the background, so the grid reads as texture, not an accent.
const ACCENT = { r: 86, g: 78, b: 112 };
// Slightly lighter tint the crest of a pulse fades toward - still muted, keeps contrast low.
const CREST = { r: 120, g: 110, b: 150 };
// A pulsing grid of squares rendered on a single canvas. Mounted once in RedesignLayout, it sits fixed
// behind every redesign page (negative z-index, under the content that lives above the layout's opaque
// background). Everything is transform/opacity-free on the DOM (zero layout shift); the motion lives
// entirely inside the canvas and is skipped for reduced-motion users (static faint grid).
export const RedesignGridBackground = ({ className }) => {
    const canvasRef = useRef(null);
    const reduceMotion = useReducedMotion();
    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) {
            return undefined;
        }
        const ctx = canvas.getContext('2d');
        if (!ctx) {
            return undefined;
        }
        let width = 0;
        let height = 0;
        let cols = 0;
        let rows = 0;
        let phases = [];
        let frame = 0;
        const step = CELL + GAP;
        // Recompute the grid dimensions + per-cell random flash phases whenever the viewport resizes.
        // Randomness only ever runs client-side here (canvas is never server-rendered), so no hydration risk.
        const resize = () => {
            const dpr = Math.min(window.devicePixelRatio || 1, 2);
            width = window.innerWidth;
            height = window.innerHeight;
            canvas.width = Math.floor(width * dpr);
            canvas.height = Math.floor(height * dpr);
            canvas.style.width = `${width}px`;
            canvas.style.height = `${height}px`;
            ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
            cols = Math.ceil(width / step) + 1;
            rows = Math.ceil(height / step) + 1;
            phases = Array.from({ length: cols * rows }, () => ({
                phase: Math.random() * Math.PI * 2,
                speed: 0.4 + Math.random() * 1.2,
            }));
        };
        const drawCell = (x, y, alpha) => {
            const clamped = Math.min(1, alpha);
            // Lerp accent -> crest as the cell brightens, so pulses read as a warm glow rather than flat opacity.
            const t = Math.min(1, clamped / (BASE_ALPHA + WAVE_ALPHA + SPARKLE_ALPHA));
            const r = Math.round(ACCENT.r + (CREST.r - ACCENT.r) * t);
            const g = Math.round(ACCENT.g + (CREST.g - ACCENT.g) * t);
            const b = Math.round(ACCENT.b + (CREST.b - ACCENT.b) * t);
            if (clamped > BLOOM_THRESHOLD) {
                ctx.globalAlpha = (clamped - BLOOM_THRESHOLD) * 0.5;
                ctx.fillStyle = `rgb(${r}, ${g}, ${b})`;
                ctx.fillRect(x - GAP / 2, y - GAP / 2, CELL + GAP, CELL + GAP);
            }
            ctx.globalAlpha = clamped;
            ctx.fillStyle = `rgb(${r}, ${g}, ${b})`;
            ctx.fillRect(x, y, CELL, CELL);
        };
        const render = (time) => {
            ctx.clearRect(0, 0, width, height);
            for (let row = 0; row < rows; row += 1) {
                for (let col = 0; col < cols; col += 1) {
                    const cell = phases[row * cols + col];
                    // eslint-disable-next-line no-continue
                    if (!cell)
                        continue;
                    // Diagonal band sweeping across the grid.
                    const wave = 0.5 + 0.5 * Math.sin(time * WAVE_SPEED - (col + row) * WAVE_FREQUENCY);
                    // Rare, sharp per-cell flash (high power keeps most cells dark most of the time).
                    const raw = 0.5 + 0.5 * Math.sin(time * WAVE_SPEED * 1.7 * cell.speed + cell.phase);
                    const sparkle = Math.pow(raw, SPARKLE_SHARPNESS) * SPARKLE_ALPHA;
                    const alpha = BASE_ALPHA + wave * WAVE_ALPHA + sparkle;
                    drawCell(col * step, row * step, alpha);
                }
            }
            ctx.globalAlpha = 1;
            frame = window.requestAnimationFrame(render);
        };
        // Static faint grid for reduced-motion users - draw once, never animate.
        const renderStatic = () => {
            ctx.clearRect(0, 0, width, height);
            for (let row = 0; row < rows; row += 1) {
                for (let col = 0; col < cols; col += 1) {
                    drawCell(col * step, row * step, BASE_ALPHA * 1.4);
                }
            }
            ctx.globalAlpha = 1;
        };
        resize();
        if (reduceMotion) {
            renderStatic();
        }
        else {
            frame = window.requestAnimationFrame(render);
        }
        const onResize = () => {
            resize();
            if (reduceMotion) {
                renderStatic();
            }
        };
        window.addEventListener('resize', onResize);
        return () => {
            window.cancelAnimationFrame(frame);
            window.removeEventListener('resize', onResize);
        };
    }, [reduceMotion]);
    return (<canvas ref={canvasRef} aria-hidden className={cn('pointer-events-none fixed inset-0 -z-10', 
        // Soften the grid toward the edges + fade it out low on the page so it never fights the content.
        '[mask-image:radial-gradient(120%_90%_at_50%_0%,black_20%,transparent_85%)]', className)}/>);
};
